// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The flat screen's answer to the wrist menu (M3-55, 06 §5).
//
// **Two of these rows were reachable only in a headset.** `endGame` and `otherMachine` are
// implemented in `MachineStage` and the *only* thing that ever called them was `onXrMenu`, so a
// player on a phone or a laptop — which is most of them — could not end a game or change machine
// once one had started. The code was written, tested and unreachable. That is the reason this
// exists; the tidier HUD is a side effect.
//
// **Three dots rather than a gear, and the difference is honesty.** A gear promises preferences.
// Most of what is in here is an *action* — pause, end this game, go and play something else — and
// a player who opens a gear expecting the settings page and finds "End game" has been misled by an
// icon. There is also a real settings page one tap away in the bottom bar, which is what a gear on
// this screen would most reasonably be taken to mean.
//
// **What stays outside it, and why that is not an oversight.** Pause, the machine's sound and the
// microphone keep their own buttons. `wristMenu.ts` already wrote the rule for the last of those —
// *"a mute control that takes two presses to reach is not a mute control"* — and the same argument
// carries, a little weaker each time, to the sound (somebody walked into the room) and to Pause (a
// ball is in play). An overflow menu is for the things a player can afford to go looking for.
//
// **`endGame` arms and confirms, exactly as it does on the wrist.** 06 §5 asks for it there, and
// the reason is the mis-tap rather than the input device: a game ended by accident is a game
// nobody gets back, whether the finger was in the air or on glass. `DESTRUCTIVE` and the arming
// window are imported from the menu model rather than restated here, so the two cannot drift about
// which rows are dangerous.
//
// **The keyboard and the screen reader are the bulk of this file.** A popup that opens on a click
// and cannot be left with Escape, or that drops focus into the page behind it, is a trap for
// exactly the players who most need the menu — and the flipper keys are still live underneath, so
// focus that escapes into the machine is focus that plays pinball at somebody.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ARM_WINDOW_MS } from '../../xr/wristMenu'
import { useLocalisation } from '../i18n/useLanguage'
import type { Translator } from '../i18n'

/** What a row does when it is chosen. `destructive` rows arm first. */
export interface StageMenuRow {
  readonly id: string
  readonly label: string
  /** The hover tooltip: what a press would do, never what is already true. */
  readonly title: string
  readonly destructive?: boolean
  readonly onChoose: () => void
}

export interface StageMenuProps {
  readonly rows: readonly StageMenuRow[]
}

export function StageMenu({ rows }: StageMenuProps): React.JSX.Element {
  const { t } = useLocalisation()
  const [open, setOpen] = useState(false)
  const [armed, setArmed] = useState<string | undefined>(undefined)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const rowIdentity = rows.map(row => row.id).join('|')

  // An End confirmation belongs to this game's menu, not a later game after idle.
  useEffect(() => { setArmed(undefined) }, [rowIdentity])

  /**
   * Close, and put focus back where it came from.
   *
   * Returning focus is the half that is always forgotten. Without it a keyboard player who opens
   * the menu, presses Escape and then presses Tab lands somewhere arbitrary — usually the top of
   * the document, behind a pinball machine they were in the middle of playing.
   */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false)
    setArmed(undefined)
    if (restoreFocus) triggerRef.current?.focus()
  }, [])

  // Escape closes, from anywhere inside. Bound to the document rather than to the panel because a
  // player who has just clicked a row still has focus inside a panel that may have re-rendered.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      // Stopped, so the same key does not also reach the machine underneath — Escape leaves
      // fullscreen, and a menu that exits focus mode on its way out is a menu that did two things.
      event.stopPropagation()
      close()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, close])

  // A press anywhere else closes it. `pointerdown` rather than `click`, so the menu is gone before
  // whatever was pressed underneath acts on it.
  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent): void => {
      const target = event.target as Node | null
      if (panelRef.current?.contains(target ?? null) === true) return
      if (triggerRef.current?.contains(target ?? null) === true) return
      // No focus restore: the player is already pressing something else, and stealing focus back
      // would undo the thing they just aimed at.
      close(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [open, close])

  // The arming window, in wall-clock time. The same three seconds 06 §5 gives the wrist menu, and
  // the same constant, so a change to one moves both.
  useEffect(() => {
    if (armed === undefined) return
    const timer = setTimeout(() => setArmed(undefined), ARM_WINDOW_MS)
    return () => clearTimeout(timer)
  }, [armed])

  // Focus the first row on open, so the menu is usable without a pointer at all.
  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector('button')?.focus()
  }, [open])

  const choose = (row: StageMenuRow): void => {
    if (row.destructive === true && armed !== row.id) {
      setArmed(row.id)
      return
    }
    // Closed **before** the action runs. Ending a game unmounts things and shows a notice; a menu
    // still open over that is a menu the player has to dismiss to read what happened.
    // Restore the stable trigger before opening another surface. The chosen popup row is
    // about to unmount and cannot be a dialog's return-focus target.
    close()
    row.onChoose()
  }

  return (
    <div className="stage__menu">
      <button
        ref={triggerRef}
        type="button"
        className="button button--quiet stage__mute stage__menuTrigger"
        title={open ? t('stage.menu-close-title') : t('stage.menu-open-title')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <DotsGlyph />
        <span className="sr-only">{open ? t('stage.menu-close') : t('stage.menu-open')}</span>
      </button>
      {open ? (
        <div className="stage__menuPanel" role="menu" aria-label={t('stage.menu-label')} ref={panelRef}>
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              role="menuitem"
              className={
                armed === row.id
                  ? 'button button--quiet stage__menuRow stage__menuRow--armed'
                  : 'button button--quiet stage__menuRow'
              }
              title={
                armed === row.id
                  ? t('stage.menu-confirm-title', { action: row.label.toLowerCase() })
                  : row.title
              }
              onClick={() => choose(row)}
            >
              {/* Lower-cased into the sentence, the way Settings does with its theme names: the
                  label is written as a row — "End this game" — and dropping it into "Really …?"
                  unedited puts a capital in the middle of a question. */}
              {armed === row.id
                ? t('stage.menu-confirm', { action: row.label.toLowerCase() })
                : row.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Three dots, drawn rather than typed.
 *
 * `⋯` and `…` are typography, not icons: they inherit the font's metrics, sit on the baseline
 * rather than in the middle of a 44 px button, and are a different width in every font a browser
 * might fall back to. An emoji would be worse again — a different picture per operating system,
 * and a box wherever the font is monochrome. Three circles are three circles everywhere.
 */
function DotsGlyph(): React.JSX.Element {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx={12} cy={5} r={1.9} />
      <circle cx={12} cy={12} r={1.9} />
      <circle cx={12} cy={19} r={1.9} />
    </svg>
  )
}

/**
 * The rows, built where the actions live.
 *
 * A function rather than a constant because every row closes over something — a navigate, a piece
 * of component state — and because the arcade rows only exist when there is an arcade. Exported so
 * a test can assert the row list without rendering a machine.
 */
export function stageMenuRows(options: {
  readonly t: Translator
  readonly gameInProgress: boolean
  readonly paused: boolean
  readonly focused: boolean
  readonly onPause: () => void
  readonly onToggleFocus: () => void
  readonly onEndGame: () => void
  readonly onAnotherMachine: () => void
  readonly onSettings: () => void
  /**
   * 11 §6's report control, when there is something to report (M9-28).
   *
   * **Absent rather than disabled for a first-party machine**, and absent is right here where
   * disabled usually is: a disabled row says "this will work later", and reporting NEON NOVA to
   * the people who wrote NEON NOVA never will. `routes.ts` draws the same line for a destination
   * that does not exist yet, which is a different thing from one that does not apply.
   */
  readonly onReport?: () => void
}): StageMenuRow[] {
  const { t } = options
  return [
    {
      id: 'pauseResume',
      // States the condition it is about to change, like every other toggle in this product.
      label: options.paused ? t('stage.menu.resume') : t('stage.menu.pause'),
      title: options.paused ? t('stage.menu.resume-title') : t('stage.menu.pause-title'),
      onChoose: options.onPause,
    },
    {
      id: 'focus',
      label: options.focused ? t('stage.focus-off') : t('stage.focus-on'),
      title: options.focused ? t('stage.focus-off-title') : t('stage.focus-on-title'),
      onChoose: options.onToggleFocus,
    },
    {
      id: 'settings',
      label: t('stage.menu.settings'),
      title: t('stage.menu.settings-title'),
      onChoose: options.onSettings,
    },
    {
      id: 'otherMachine',
      label: t('stage.menu.another-machine'),
      title: t('stage.menu.another-machine-title'),
      onChoose: options.onAnotherMachine,
    },
    ...(options.onReport === undefined
      ? []
      : [
          {
            id: 'report',
            label: t('stage.menu.report'),
            title: t('stage.menu.report-title'),
            onChoose: options.onReport,
          },
        ]),
    {
      // The one destructive row, and the only one in either menu. Arms on the first press.
      id: 'endGame',
      label: t('stage.menu.end-game'),
      title: t('stage.menu.end-game-title'),
      destructive: true,
      onChoose: options.onEndGame,
    },
  ].filter(row => options.gameInProgress || (row.id !== 'pauseResume' && row.id !== 'endGame'))
}
